import React from 'react'
import './assets/base.scss'

import Main from './core/templates/Main'
import { HashRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { applyMiddleware, createStore, Store } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import { appReducer, AppState } from './store'
import { createLogger } from 'redux-logger'
import thunk from 'redux-thunk'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import ja from '@gunosy/media-admin-components/locales/ja.json'
import withApollo from './withApollo'
import withAuthN from './withAuthN'
import withAuthZ from './withAuthZ'
import * as Sentry from '@sentry/react'
import { CaptureConsole } from '@sentry/integrations';

const logger = (createLogger as any)()
const middleware =
  process.env.NODE_ENV === 'development' ? composeWithDevTools(applyMiddleware(logger, thunk)) : applyMiddleware(thunk)
const store = createStore(appReducer, {}, middleware) as Store<AppState>
const MainApp = withAuthN(withApollo(withAuthZ(Main)))

if (process.env.REACT_APP_ENV !== 'local') {
  Sentry.init({
    dsn: 'https://55db998680d4410eb0cfa658667a98c4@o125271.ingest.sentry.io/5276868',
    integrations: [
      new CaptureConsole({
        levels: ['error'],
      }),
    ],
    environment: process.env.REACT_APP_ENV,
    release: `media-admin-insight@${process.env.REACT_APP_BUILD_VERSION}`,
    // Will cause a deprecation warning, but the demise of `ignoreErrors` is still under discussion.
    // See: https://github.com/getsentry/raven-js/issues/73
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Generic error code from errors outside the security sandbox
      // You can delete this if using raven.js > 1.0, which ignores these automatically.
      'Script error.',
    ],
    ignoreUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  } as Sentry.BrowserOptions)
}

i18n.use(initReactI18next).init({
  resources: {
    ja: { translation: ja },
  },
  lng: 'ja',
  fallbackLng: 'ja',
  interpolation: { escapeValue: false },
  nsSeparator: '::',
})

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <HashRouter>
        <MainApp />
      </HashRouter>
    </Provider>
  )
}

export default App
